import React, { useState, useMemo } from 'react';
import { Box, TextField, Button, Grid, Stack, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const caseTransformations = {
  'Sentence case': (s) => s.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, c => c.toUpperCase()),
  'lower case': (s) => s.toLowerCase(),
  'UPPER CASE': (s) => s.toUpperCase(),
  'Title Case': (s) => s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
  'camelCase': (s) => s.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase())).replace(/\s+/g, ''),
  'PascalCase': (s) => s.replace(/(?:^\w|[A-Z]|\b\w)/g, word => word.toUpperCase()).replace(/\s+/g, ''),
  'kebab-case': (s) => s.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase().replace(/\s+/g, '-'),
  'snake_case': (s) => s.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1_$2').toLowerCase().replace(/\s+/g, '_'),
  'iNVERSE cASE': (s) => s.split('').map(c => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join(''),
};

function CaseConverter() {
  const [text, setText] = useState('Hello World');

  const stats = useMemo(() => {
    const characters = text.length;
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    const lines = text.split('\n').length;
    return { characters, words, lines };
  }, [text]);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
  };

  return (
    <>
      <SEO
        title="Case Converter | Change Text Case Online | UmbrellaCo"
        description="Easily convert text between upper case, lower case, title case, camelCase, snake_case, and more. Free online text formatting tool."
        keywords="case converter, upper case, lower case, camel case generator, snake case, text formatting"
        url="/text-tools/case-converter"
        jsonLd={createSoftwareAppSchema(
          'Case Converter',
          'Instantly format and convert text cases for code and content.',
          'https://umbrellaco.in/text-tools/case-converter',
          ['Text Formatting', 'Case Conversion', 'String Manipulation']
        )}
      />
      <ToolPageContainer
        title="Case Converter"
        description="Instantly transform your text or code strings into various cases like camelCase, snake_case, Title Case, and more."
      >
      
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <TextField
          multiline
          value={text}
          onChange={(e) => setText(e.target.value)}
          variant="outlined"
          fullWidth
          sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { height: '100%' }, '& textarea': { height: '100% !important' } }}
        />
        <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <Tooltip title="Copy">
            <IconButton onClick={handleCopy} size="small"><ContentCopyIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Clear">
            <IconButton onClick={() => setText('')} size="small"><ClearIcon /></IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Stack direction="row" spacing={2} sx={{ my: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }} justifyContent="center">
        <Typography variant="body2">Characters: {stats.characters}</Typography>
        <Divider orientation="vertical" flexItem />
        <Typography variant="body2">Words: {stats.words}</Typography>
        <Divider orientation="vertical" flexItem />
        <Typography variant="body2">Lines: {stats.lines}</Typography>
      </Stack>

      <Grid container spacing={1}>
        {Object.entries(caseTransformations).map(([name, func]) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={name}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setText(func(text))}
              sx={{ textTransform: 'none' }}
            >
              {name}
            </Button>
          </Grid>
        ))}
      </Grid>
      </ToolPageContainer>
    </>
  );
}

export default CaseConverter;